import React, { useState, useEffect, useRef } from 'react';
import {
  Users,
  Mail,
  CheckCircle,
  Brain,
  Star,
  FileText,
  Calendar,
  Send,
  ChevronRight,
  Award,
  Target,
  Clock,
  Sparkles,
  MessageSquare
} from 'lucide-react';

const RecommendationDemo = ({ data }) => {
  const [selectedRecommender, setSelectedRecommender] = useState(null);
  const [showBragSheet, setShowBragSheet] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  const recommenders = [
    {
      id: 1,
      name: "Dr. Martinez",
      subject: "AP Physics C",
      relationship: "Taught Sheppard for 2 years, Engineering Club advisor",
      matchScore: 98,
      strengths: ["Problem-solving", "Leadership", "Technical skills"],
      status: "ready",
      lastContact: "2 weeks ago",
      insights: "Can speak to engineering mindset and bridge project leadership"
    },
    {
      id: 2,
      name: "Ms. Thompson",
      subject: "City Planning Commission",
      relationship: "Supervised Sheppard's infrastructure review work",
      matchScore: 95,
      strengths: ["Real-world impact", "Maturity", "Community focus"],
      status: "requested",
      lastContact: "3 days ago",
      insights: "Unique perspective on professional-level contributions"
    },
    {
      id: 3,
      name: "Mr. Chen",
      subject: "AP Calculus BC",
      relationship: "Math team coach, taught advanced topics",
      matchScore: 88,
      strengths: ["Analytical thinking", "Competition success", "Mentorship"],
      status: "ready",
      lastContact: "1 month ago",
      insights: "State math competition victory and peer tutoring"
    }
  ];

  const handleSendRequest = () => {
    setEmailSent(true);
    setTimeout(() => setEmailSent(false), 3000);
  };

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-50 rounded-full mb-4">
          <Users className="w-4 h-4 text-orange-600" />
          <span className="text-sm font-medium text-orange-700">
            Strategic Recommendations
          </span>
        </div>
        <h2 className="text-4xl font-bold text-slate-900 mb-4">
          Orchestrate Compelling Endorsements
        </h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          We identify optimal recommenders, generate personalized requests with strategic talking points,
          and provide comprehensive brag sheets that ensure powerful, aligned letters.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: Recommender Selection */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-lg border border-slate-200">
            {/* Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900 text-lg">
                  Optimal Recommender Analysis
                </h3>
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-violet-500" />
                  <span className="text-sm text-slate-600">AI-Matched</span>
                </div>
              </div>
            </div>

            {/* Recommender Cards */}
            <div className="p-6 space-y-4">
              {recommenders.map((recommender, index) => (
                <div
                  key={recommender.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-all
                    ${selectedRecommender?.id === recommender.id
                      ? 'ring-2 ring-orange-500 bg-orange-50/50 border-orange-300'
                      : 'hover:shadow-md border-slate-200'}`}
                  onClick={() => {
                    setSelectedRecommender(recommender);
                    setShowBragSheet(true);
                  }}
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: isInView ? 'fadeInSlide 0.6s ease-out forwards' : 'none'
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-slate-900">{recommender.name}</h4>
                        <span className="text-sm text-slate-600">{recommender.subject}</span>
                        {recommender.status === 'requested' && (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Requested
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-slate-600 mb-3">
                        {recommender.relationship}
                      </p>

                      <div className="flex items-center gap-4">
                        {/* Match Score */}
                        <div className="flex items-center gap-2">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3 h-3 ${
                                  i < Math.floor(recommender.matchScore / 20)
                                    ? 'text-yellow-500 fill-current'
                                    : 'text-slate-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-xs font-medium text-slate-700">
                            {recommender.matchScore}% match
                          </span>
                        </div>

                        {/* Key Strengths */}
                        <div className="flex gap-2">
                          {recommender.strengths.slice(0, 2).map((strength, i) => (
                            <span
                              key={i}
                              className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded"
                            >
                              {strength}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Insight */}
                      <div className="mt-3 p-2 bg-azure-50 rounded border border-azure-200">
                        <div className="flex items-start gap-2">
                          <Sparkles className="w-3 h-3 text-azure-500 mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-azure-700">{recommender.insights}</span>
                        </div>
                      </div>
                    </div>

                    <ChevronRight className={`w-5 h-5 transition-transform
                      ${selectedRecommender?.id === recommender.id ? 'rotate-90 text-orange-600' : 'text-slate-400'}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Request Generator & Brag Sheet */}
        <div className="space-y-4">
          {/* Request Email Generator */}
          {selectedRecommender && (
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6"
              style={{
                animation: 'fadeInScale 0.4s ease-out forwards'
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-5 h-5 text-azure-600" />
                <h3 className="font-semibold text-slate-900">Request Email</h3>
              </div>

              <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 mb-4 max-h-48 overflow-y-auto">
                <div className="font-medium mb-2">Dear {selectedRecommender.name},</div>
                <div className="space-y-2 text-xs leading-relaxed">
                  <p>
                  I'm reaching out to ask if you'd be willing to write a recommendation letter for my college applications. I've been thinking about which teachers really know me as a student, and you keep coming to mind. I feel like I grew the most in your{selectedRecommender.subject} class, especially in {selectedRecommender.strengths[0]}.
                  </p>
                  <p>
                    Your perspective on my academic character and growth is invaluable.
                  </p>
                  <p>
                    I've attached a brag sheet with specific examples that might be helpful, including our
                    work on the pedestrian bridge proposal that won the ASCE competition.
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleSendRequest}
                  className="flex-1 px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors flex items-center justify-center gap-2"
                >
                  {emailSent ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Sent!
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Request
                    </>
                  )}
                </button>
                <button className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">
                  Edit
                </button>
              </div>
            </div>
          )}

          {/* Brag Sheet Preview */}
          {showBragSheet && (
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-slate-900">Brag Sheet</h3>
                </div>
                <span className="text-xs text-emerald-600 font-medium">Auto-Generated</span>
              </div>

              <div className="space-y-3 text-sm">
                <div>
                  <div className="font-medium text-slate-900 mb-1">Engineering Excellence</div>
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li>• ASCE Bridge Building National Champion</li>
                    <li>• City Planning Commission - youngest member</li>
                    <li>• Engineering Club President (15 members)</li>
                  </ul>
                </div>

                <div>
                  <div className="font-medium text-slate-900 mb-1">Leadership Impact</div>
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li>• Habitat for Humanity: trained 30+ volunteers</li>
                    <li>• Math Team Captain: led team to state finals</li>
                    <li>• Designed pedestrian bridge adopted by city</li>
                  </ul>
                </div>

                <div>
                  <div className="font-medium text-slate-900 mb-1">Academic Achievement</div>
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li>• GPA: 3.92/4.0 (4.45 weighted)</li>
                    <li>• SAT: 1540 | ACT: 35</li>
                    <li>• AP Scholar with Distinction</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Timeline Reminder */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-red-500" />
              <h3 className="font-semibold text-slate-900">Timeline</h3>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">MIT Deadline</span>
                <span className="font-medium text-slate-900">Jan 1</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Request Letters By</span>
                <span className="font-medium text-orange-600">Nov 15</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Follow-up Reminder</span>
                <span className="font-medium text-slate-900">Dec 1</span>
              </div>
            </div>

            <div className="mt-3 text-xs text-slate-500">
              Give recommenders at least 6 weeks notice
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInSlide {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default RecommendationDemo;